import React from 'react';
import { RiAttachmentLine, RiCloseLine, RiPlayFill, RiStopFill, RiArrowDownSLine } from 'react-icons/ri';

const TOOL_BTN = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--text-secondary, #888)', padding: '4px',
  display: 'flex', alignItems: 'center', borderRadius: '6px', flexShrink: 0,
  transition: 'color 0.15s',
};

const AskMode = ({
  question,
  setQuestion,
  handleKeyDown,
  textareaStyle,
  askModeHint,
  files,
  fileObjectUrls,
  removeFile,
  loading,
  cancelRequest,
  stopAsk,
  isActiveConvServerRunning = false,
  isMobileView,
  fileInputRef,
  askQuestion,
  placeholder = 'Write your question here...',
  submitDisabled = false,
  hideFileUpload = false,
  activeMode = 'ask',
  onHide,
}) => {
  const isStopMode = loading || isActiveConvServerRunning;
  const handleStopClick = loading
    ? () => { cancelRequest(true); stopAsk(); }
    : stopAsk;

  return (
    <form onSubmit={askQuestion}>
      {/* Attached file chips */}
      {files.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
          {files.map((file, index) => (
            <div key={index} style={{
              display: 'flex', alignItems: 'center',
              backgroundColor: 'var(--bg-secondary, #f0f0f0)',
              padding: '4px 8px', borderRadius: '4px',
              fontSize: '0.9rem', border: '1px solid var(--border-color, #ddd)',
            }}>
              <span
                style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', textDecoration: 'underline', color: 'var(--primary-color, #636cff)' }}
                onClick={() => { const url = fileObjectUrls.get(index); if (url) window.open(url, '_blank'); }}
                title="Click to view file"
              >
                {file.name}
              </span>
              <button type="button" aria-label={`Remove ${file.name}`} onClick={() => removeFile(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginLeft: 4, display: 'flex', alignItems: 'center', color: 'var(--text-secondary, #666)' }}><RiCloseLine size={16} /></button>
            </div>
          ))}
        </div>
      )}

      {/* ── Unified input box: textarea + toolbar inside same container ── */}
      <div style={{
        border: '1px solid var(--input-border, rgba(255,255,255,0.15))',
        borderRadius: '6px',
        background: 'var(--input-background)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Textarea — no border, transparent bg */}
        <textarea
          placeholder={placeholder}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: isMobileView ? '0.5rem' : '0.75rem',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            resize: 'none',
            fontSize: isMobileView ? '1rem' : '1.1rem',
            lineHeight: '1.4',
            minHeight: isMobileView ? '2rem' : '2.5rem',
            color: 'inherit',
            fontFamily: 'inherit',
          }}
          disabled={isStopMode}
          required={files.length === 0}
          rows={isMobileView ? 1 : 2}
        />

        {/* Toolbar — inside the box */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 6px 6px', gap: '6px' }}>

          {/* Left: attach + workflow link hint */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, overflow: 'hidden' }}>
            {!hideFileUpload && (
              <button
                type="button"
                style={TOOL_BTN}
                onClick={() => fileInputRef.current.click()}
                disabled={isStopMode}
                title="Attach file (PNG, JPG, WEBP, PDF, TXT)"
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--primary-color, #636cff)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary, #888)'; }}
              >
                <RiAttachmentLine size={18} />
              </button>
            )}
            {activeMode === 'workflow' && (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #888)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {askModeHint}
              </span>
            )}
          </div>

          {/* Right: hide + submit */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>

            {/* Submit / Stop */}
            <button
              type={isStopMode ? 'button' : 'submit'}
              onClick={isStopMode ? handleStopClick : undefined}
              disabled={submitDisabled}
              style={{
                background: isStopMode ? 'var(--color-danger, #dc3545)' : 'var(--primary-color, #636cff)',
                border: 'none', borderRadius: '8px', width: '32px', height: '32px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: submitDisabled ? 'not-allowed' : 'pointer',
                color: '#fff', flexShrink: 0, opacity: submitDisabled ? 0.5 : 1,
                transition: 'background 0.15s',
              }}
              title={isStopMode ? 'Stop Thinking' : activeMode === 'workflow' ? 'Run' : 'Ask'}
            >
              {isStopMode ? <RiStopFill size={15} /> : <RiPlayFill size={15} />}
            </button>

            {/* Collapse card — always last/rightmost */}
            {onHide && (
              <button type="button" style={TOOL_BTN} onClick={onHide} title="Hide input panel"
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--primary-color, #636cff)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary, #888)'; }}>
                <RiArrowDownSLine size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </form>
  );
};

export default AskMode;
